// Резервациите на един гост в модала на справка „Гости" — само таблицата:
// колоните и клетките на „Дневник на резервациите" (същите рендери) и редът
// „Общо". БЕЗ лентата с инструменти, филтри, избор на колони, Excel и
// страници — модалът е за поглед, не за работа с таблица, и не заема място
// с бутони. Редовете идват подредени от BE (най-новата горе).

import { useMemo } from "react";
import { flexRender, getCoreRowModel, getFilteredRowModel, useReactTable } from "@tanstack/react-table";

import { DataCell, TotalsRow } from "../../../../components/dataTable";
import type { ReservationJournalRow } from "../../../../api/reportsReservations";
import { formatMomentWithDate } from "./reservationReportFormat";
import { useJournalColumns } from "./journalColumns";

// Какво се вижда за всяко посещение — в този ред. Името и телефонът са в
// хедъра на модала; кой е създал/настанил и кога — в „Дневник".
const VISIBLE_COLUMNS = [
  "start_at",
  "guests",
  "tables",
  "status",
  "reservation_note",
  "duration_minutes",
  "seated_minutes",
  "bill_total",
];
// Забележката взима мястото, което остава, и се реже в него — таблицата се
// побира в модала без хоризонтален скрол; другите колони са по съдържанието си.
const FLEXIBLE_COLUMN = "reservation_note";
const MONO = "font-pos-mono text-[12.5px] font-semibold tabular-nums";

// Като клетката „Час" в „Дневник", но само с началото — колко са стояли
// казва „Реален престой", а колко е била резервацията — „Продължителност".
const startCell = ({ getValue }: { getValue: () => unknown }) => (
  <span className={`${MONO} text-ink`}>{formatMomentWithDate(getValue() as string)}</span>
);

const alignClass = (align: string | undefined, kind: string | undefined) =>
  align === "right" || (align === undefined && kind !== undefined && ["num", "money", "pct", "bar"].includes(kind))
    ? "text-right"
    : "text-left";

export const GuestVisitsTable = ({ rows, ariaLabel }: { rows: ReservationJournalRow[]; ariaLabel: string }) => {
  const journalColumns = useJournalColumns();
  const columns = useMemo(
    () =>
      VISIBLE_COLUMNS.flatMap((id) => {
        const column = journalColumns.find((candidate) => candidate.id === id);
        if (!column) return [];
        return [id === "start_at" ? { ...column, cell: startCell } : column];
      }),
    [journalColumns],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => String(row.id),
    getCoreRowModel: getCoreRowModel(),
    // Редът „Общо" сумира филтрираните — тук филтри няма, значи всички.
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="overflow-x-auto rounded-[12px] border border-shade-2 bg-surface">
      <table aria-label={ariaLabel} className="w-full border-collapse text-left">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                return (
                  <th
                    key={header.id}
                    scope="col"
                    className={`whitespace-nowrap border-b border-shade-2 bg-panel px-3 py-2 font-pos-mono text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted ${alignClass(
                      meta?.align,
                      meta?.cell?.kind,
                    )} ${header.column.id === FLEXIBLE_COLUMN ? "w-full" : ""}`}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="border-b border-shade-1">
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta;
                const align = alignClass(meta?.align, meta?.cell?.kind);
                return (
                  <td
                    key={cell.id}
                    // `max-w-0` + `w-full`: гъвкавата колона взима остатъка и реже текста
                    // (целият е при посочване — `fullTextOnHover` на клетката), вместо да
                    // разпъва таблицата; `min-w` — на телефон да не се свие до нищо.
                    className={`h-[45px] whitespace-nowrap px-3 align-middle leading-[normal] ${align} ${
                      cell.column.id === FLEXIBLE_COLUMN ? "w-full min-w-[160px] max-w-0" : ""
                    }`}
                  >
                    {meta?.cell ? (
                      <span className={`flex ${align === "text-right" ? "justify-end" : "justify-start"}`}>
                        <DataCell options={meta.cell} value={cell.getValue()} />
                      </span>
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <TotalsRow table={table} density="normal" rowCount={rows.length} />
      </table>
    </div>
  );
};
